import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  TextField,
  TablePagination,
  TableSortLabel,
  Button,
} from "@mui/material";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"; // ⭐ ADDED (Jira-style date icon)

import { useNavigate } from "react-router-dom";

import api from "../../../api/api";
import { useAuth } from "../../../context/AuthContext";

export default function OverviewTab({ projectId, project }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [metrics, setMetrics] = useState({
    teamCount: 0,
    openTasks: 0,
    completion: 0,
  });

  const [items, setItems] = useState([]);

  const [teamUsers, setTeamUsers] = useState([]);
  // ⭐ ADDED: clients list for client_id mapping
  const [clients, setClients] = useState([]);

  const [taskSearch, setTaskSearch] = useState("");

  const [filters, setFilters] = useState({
    status: "IN_PROGRESS",
    assignee: "",
    assigned_from: "",
    assigned_to: "",
  });

  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ⭐ ADDED: track which row is editing development date
  const [editingDateId, setEditingDateId] = useState(null);

  useEffect(() => {
    loadOverview();
    //loadUsers();
    loadClients(); // ⭐ ADDED
     loadTeamUsers(); // ✅ ADD THIS
  }, [projectId]);

  /* ====================================== */
  /* DATE FORMAT */
  /* ====================================== */
  useEffect(() => {
    if (user?.id) {
      setFilters((prev) => ({
        ...prev,
        assignee: user?.id || user?.user?.id,
      }));
    }
  }, [user]);
  const formatDate = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const day = String(date.getDate()).padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  /* ====================================== */
  /* STATUS COLOR */
  /* ====================================== */

  const getStatusStyle = (status) => {
    switch (status) {
      case "OPEN":
        return { backgroundColor: "#e0e0e0", fontWeight: 600 };

      case "IN_PROGRESS":
        return { backgroundColor: "#fff176", fontWeight: 600 };

      case "DONE":
        return { backgroundColor: "#a5d6a7", fontWeight: 600 };

      default:
        return {};
    }
  };

  /* ====================================== */
  /* LOAD DATA */
  /* ====================================== */

  const loadOverview = async () => {
    try {
      const projectRes = await api.get(`/projects/${projectId}`);
      console.log("PROJECT RESPONSE:", projectRes.data);
      const members = projectRes.data.members || [];

      const workRes = await api.get(`/work-items/?project_id=${projectId}`);
      const workItems = workRes.data || [];

      setItems(workItems);

      const openTasks = workItems.filter((i) => i.status !== "DONE").length;
      const completed = workItems.filter((i) => i.status === "DONE").length;

      const completion =
        workItems.length === 0
          ? 0
          : Math.round((completed / workItems.length) * 100);

      setMetrics({
        teamCount: members.length,
        openTasks,
        completion,
      });
    } catch (err) {
      console.error(err);
    }
  };

  /* ====================================== */
  /* LOAD USERS */
  /* ====================================== */

  /* ====================================== */
  /* LOAD TEAM MEMBERS (PROJECT ONLY) */
  /* ====================================== */
	const loadTeamUsers = async () => {
  try {
    const res = await api.get(`/projects/${projectId}/team-users`);

    console.log("TEAM USERS:", res.data);

    setTeamUsers(res.data || []);
  } catch (err) {
    console.error("Error loading team users", err);
  }
};
  /* ====================================== */
  /* LOAD TEAM MEMBERS (PROJECT ONLY) */
  /* ====================================== */

  //const [teamUsers, setTeamUsers] = useState([]);
  
  /* ====================================== */
  /* ⭐ ADDED: LOAD CLIENTS */
  /* ====================================== */

  const loadClients = async () => {
    try {
      const res = await api.get("/billing/clients");

      setClients(res.data || []);
    } catch (err) {
      console.error("Error loading clients", err);
    }
  };

  /* ====================================== */
  /* ⭐ ADDED: GET CLIENT NAME */
  /* ====================================== */

  const getClientName = (clientId) => {
    const client = clients.find((c) => String(c.id) === String(clientId));

    return client ? client.name : "-";
  };
  /* ====================================== */
  /* STATUS UPDATE */
  /* ====================================== */

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/work-items/${id}/status`, { status });
      loadOverview();
    } catch (err) {
      console.error(err);
    }
  };

  /* ====================================== */
  /* ASSIGNEE UPDATE */
  /* ====================================== */

  const changeAssignee = async (id, userId) => {
    try {
      await api.patch(`/work-items/${id}/assign`, { assignee_id: userId });

      loadOverview();
    } catch (err) {
      console.error(err);
    }
  };

  /* ====================================== */
  /* DEVELOPMENT DATE UPDATE */
  /* ====================================== */

  const updateDevelopmentDate = async (id, date) => {
    try {
      await api.patch(`/work-items/${id}/development-date`, {
        development_date: date,
      });

      setEditingDateId(null); // ⭐ close editor after save

      loadOverview();
    } catch (err) {
      console.error(err);
    }
  };

  /* ====================================== */
  /* FILTERING */
  /* ====================================== */

  const filtered = items.filter((i) => {
    return (
      (!filters.status || i.status === filters.status) &&
      (!filters.assignee ||
        String(i.assignee_id) === String(filters.assignee)) &&
      (!taskSearch ||
        (i.title || "").toLowerCase().includes(taskSearch.toLowerCase())) &&
      (!filters.assigned_from ||
        (i.created_at && i.created_at >= filters.assigned_from)) &&
      (!filters.assigned_to ||
        (i.created_at && i.created_at <= filters.assigned_to))
    );
  });

  /* ====================================== */
  /* SORTING */
  /* ====================================== */

  const sorted = [...filtered].sort((a, b) => {
    if (order === "asc") return a[orderBy] > b[orderBy] ? 1 : -1;

    return a[orderBy] < b[orderBy] ? 1 : -1;
  });

  const paginated = sorted.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === "asc";

    setOrder(isAsc ? "desc" : "asc");

    setOrderBy(field);
  };

  /* ====================================== */
  /* EXCEL EXPORT */
  /* ====================================== */

  const exportToExcel = () => {
    const data = filtered.map((item) => ({
      "Work Item ID": item.work_item_code,
      Task: item.title,
      Status: item.status,
      Assignee: item.assignee_name,
      "Assigned Date": formatDate(item.created_at),
      "Development Date": formatDate(item.development_date),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "WorkItems");

    XLSX.writeFile(workbook, "work_items.xlsx");
  };

  const MetricCard = ({ title, value }) => (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>

        <Typography variant="h5" fontWeight={600}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6">Project Description</Typography>

          <Typography color="text.secondary">
            {project.description || "No description provided"}
          </Typography>

          <Typography color="error" mt={1}>
            Project type — {project.project_type}
          </Typography>

          {/* ⭐ ADDED: CLIENT NAME */}
          {project.client_id && (
            <Typography color="primary" mt={1}>
              Client — {getClientName(project.client_id)}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <MetricCard title="Team Members" value={metrics.teamCount} />
        </Grid>

        <Grid item xs={12} md={4}>
          <MetricCard title="Open Tasks" value={metrics.openTasks} />
        </Grid>

        <Grid item xs={12} md={4}>
          <MetricCard title="Completion" value={`${metrics.completion}%`} />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Work Items Report
          </Typography>

          <Button variant="contained" sx={{ mb: 2 }} onClick={exportToExcel}>
            Export Excel
          </Button>

          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <Select
              size="small"
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
            >
              <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="OPEN">OPEN</MenuItem>
              <MenuItem value="DONE">DONE</MenuItem>
            </Select>

            <Select
              size="small"
              value={filters.assignee}
              displayEmpty
              onChange={(e) =>
                setFilters({ ...filters, assignee: e.target.value })
              }
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All Assignees</MenuItem>

{teamUsers.map((u) => (
  <MenuItem key={u.id} value={u.id}>
    {u.name}
  </MenuItem>
))}
            </Select>

            <TextField
              size="small"
              label="Search Task"
              value={taskSearch}
              onChange={(e) => setTaskSearch(e.target.value)}
            />

            <TextField
              size="small"
              type="date"
              label="Assigned From"
              InputLabelProps={{ shrink: true }}
              onChange={(e) =>
                setFilters({ ...filters, assigned_from: e.target.value })
              }
            />

            <TextField
              size="small"
              type="date"
              label="Assigned To"
              InputLabelProps={{ shrink: true }}
              onChange={(e) =>
                setFilters({ ...filters, assigned_to: e.target.value })
              }
            />
          </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Work Item ID</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assignee</TableCell>
                <TableCell>Assigned Date</TableCell>
                <TableCell>Development Date</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginated.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell>{index + 1}</TableCell>

                  <TableCell>
                    <span
                      style={{
                        color: "#d32f2f",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => navigate(`/work-items/${item.id}`)}
                    >
                      {item.work_item_code}
                    </span>
                  </TableCell>

                  <TableCell>{item.title}</TableCell>

                  <TableCell>
                    <Select
                      size="small"
                      value={item.status}
                      onChange={(e) => changeStatus(item.id, e.target.value)}
                      sx={getStatusStyle(item.status)}
                    >
                      <MenuItem value="OPEN">OPEN</MenuItem>
                      <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
                      <MenuItem value="DONE">DONE</MenuItem>
                    </Select>
                  </TableCell>

                  <TableCell>
                    <Select
                      size="small"
                      value={item.assignee_id || ""}
                      onChange={(e) => changeAssignee(item.id, e.target.value)}
                    >
                      <MenuItem value="">Unassigned</MenuItem>

{teamUsers.map((u) => (
  <MenuItem key={u.id} value={u.id}>
    {u.name}
  </MenuItem>
))}
                    </Select>
                  </TableCell>

                  <TableCell>{formatDate(item.created_at)}</TableCell>

                  {/* ⭐ JIRA STYLE DEVELOPMENT DATE */}

                  <TableCell>
                    {editingDateId === item.id ? (
                      <TextField
                        type="date"
                        size="small"
                        value={
                          item.development_date
                            ? item.development_date.substring(0, 10)
                            : ""
                        }
                        onChange={(e) =>
                          updateDevelopmentDate(item.id, e.target.value)
                        }
                        autoFocus
                      />
                    ) : (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { color: "#1976d2" },
                        }}
                        onClick={() => setEditingDateId(item.id)}
                      >
                        {formatDate(item.development_date)}

                        <CalendarMonthIcon fontSize="small" />
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) =>
              setRowsPerPage(parseInt(e.target.value))
            }
          />
        </CardContent>
      </Card>
    </Box>
  );
}
